/** Maps comparison operators to their logical inverses. */
export const COMPARE_INVERT: Record<string, string> = {
  '==': '!=', '!=': '==', '<': '>=', '>': '<=', '<=': '>', '>=': '<',
  '<u': '>=u', '>u': '<=u', '<=u': '>u', '>=u': '<u',
};

/**
 * Sentinel `target` values on a `call` instruction for callees that are not module functions.
 * A real call uses a non-negative target (the function index); these negative sentinels mark
 * built-in operations and intrinsics, whose display name comes from `SsaFunction.intrinsicNames`.
 */
export const CallTarget = {
  MemorySize: -1,
  MemoryGrow: -2,
  AtomicCompareExchange: -3,
  /** A result-bearing intrinsic; its name is keyed in intrinsicNames by the result variable id. */
  ResultIntrinsic: -4,
  /** Void intrinsics use a distinct target at or below this, keyed in intrinsicNames by the target. */
  FirstVoidIntrinsic: -100,
} as const;

export interface SsaVariable {
  id: number;
  name: string;
  type: string;
  definedInBlock: number;
}

export type SsaValue = SsaVariable | SsaConst;

export interface SsaConst {
  kind: 'const';
  value: number | bigint;
  type: string;
}

export function isVariable(value: SsaValue): value is SsaVariable {
  return 'id' in value;
}

export function isConst(value: SsaValue): value is SsaConst {
  return 'kind' in value && (value as SsaConst).kind === 'const';
}

export type SsaInstr =
  | { kind: 'phi'; result: SsaVariable; inputs: { blockId: number; value: SsaValue }[] }
  | { kind: 'assign'; result: SsaVariable; value: SsaValue }
  | { kind: 'const'; result: SsaVariable; value: number | bigint; type: string }
  | { kind: 'binary'; result: SsaVariable; op: string; left: SsaValue; right: SsaValue }
  | { kind: 'unary'; result: SsaVariable; op: string; operand: SsaValue }
  | { kind: 'compare'; result: SsaVariable; op: string; left: SsaValue; right: SsaValue }
  | { kind: 'load'; result: SsaVariable; address: SsaValue; offset: number; loadType: string }
  | { kind: 'store'; address: SsaValue; value: SsaValue; offset: number; storeType: string }
  | { kind: 'call'; result: SsaVariable | null; target: number; args: SsaValue[] }
  | { kind: 'call_indirect'; result: SsaVariable | null; tableIndex: SsaValue; typeIndex: number; args: SsaValue[] }
  | { kind: 'convert'; result: SsaVariable; op: string; operand: SsaValue }
  | { kind: 'select'; result: SsaVariable; condition: SsaValue; trueVal: SsaValue; falseVal: SsaValue }
  | { kind: 'global_get'; result: SsaVariable; globalIndex: number }
  | { kind: 'global_set'; globalIndex: number; value: SsaValue }
  | { kind: 'branch'; target: number }
  | { kind: 'branch_if'; condition: SsaValue; trueTarget: number; falseTarget: number }
  | { kind: 'branch_table'; selector: SsaValue; targets: number[]; defaultTarget: number }
  | { kind: 'return'; values: SsaValue[] }
  | { kind: 'unreachable' };

export interface SsaBlock {
  id: number;
  instructions: SsaInstr[];
  successors: number[];
  predecessors: number[];
}

export interface SsaFunction {
  blocks: SsaBlock[];
  variables: SsaVariable[];
  paramCount: number;
  localCount: number;
  intrinsicNames: Map<number, string>;
  entryBlockId: number;
  exitBlockId: number;
}
